import type { BroadcastMessage, BroadcastChannel } from '@/mocks/superAdminBroadcastMessages';
import { broadcastChannels } from '@/mocks/superAdminBroadcastMessages';

interface BroadcastActionModalProps {
  message: BroadcastMessage | null;
  onClose: () => void;
  onView: (msg: BroadcastMessage) => void;
  onEdit: (msg: BroadcastMessage) => void;
  onDuplicate: (msg: BroadcastMessage) => void;
  onSchedule: (msg: BroadcastMessage) => void;
  onResend: (msg: BroadcastMessage) => void;
  onViewStats: (msg: BroadcastMessage) => void;
  onCancelSend: (msg: BroadcastMessage) => void;
  onDelete: (msg: BroadcastMessage) => void;
}

const statusBadge: Record<BroadcastMessage['status'], { label: string; cls: string; icon: string }> = {
  draft: { label: 'Borrador', cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: 'ri-draft-line' },
  scheduled: { label: 'Programado', cls: 'bg-sky-50 text-sky-700 border-sky-200', icon: 'ri-time-line' },
  sent: { label: 'Enviado', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: 'ri-check-double-line' },
  cancelled: { label: 'Cancelado', cls: 'bg-red-50 text-red-700 border-red-200', icon: 'ri-close-circle-line' },
};

const channelIcon: Record<BroadcastChannel, string> = {
  email: 'ri-mail-line',
  notification: 'ri-notification-3-line',
  push: 'ri-smartphone-line',
  banner: 'ri-layout-top-line',
  whatsapp: 'ri-whatsapp-line',
};

const channelColor: Record<BroadcastChannel, string> = {
  email: 'text-sky-500',
  notification: 'text-amber-500',
  push: 'text-purple-400',
  banner: 'text-emerald-500',
  whatsapp: 'text-green-500',
};

const channelBg: Record<BroadcastChannel, string> = {
  email: 'bg-sky-50',
  notification: 'bg-amber-50',
  push: 'bg-purple-50',
  banner: 'bg-emerald-50',
  whatsapp: 'bg-green-50',
};

export default function BroadcastActionModal({
  message,
  onClose,
  onView,
  onEdit,
  onDuplicate,
  onSchedule,
  onResend,
  onViewStats,
  onCancelSend,
  onDelete,
}: BroadcastActionModalProps) {
  if (!message) return null;

  const isDraft = message.status === 'draft';
  const isScheduled = message.status === 'scheduled';
  const isSent = message.status === 'sent';
  const isCancelled = message.status === 'cancelled';
  const channelLabel = broadcastChannels.find((c) => c.value === message.channel)?.label ?? message.channel;
  const sb = statusBadge[message.status];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-sm bg-background-50 border border-background-200 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-background-200">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${channelBg[message.channel]}`}>
            <i className={`${channelIcon[message.channel]} ${channelColor[message.channel]} text-lg`}></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-foreground-800 text-sm font-bold truncate">{message.subject}</p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-foreground-400 text-[10px]">{channelLabel}</span>
              <span className="text-foreground-300 text-[10px]">·</span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${sb.cls}`}>
                <i className={`${sb.icon} text-[9px]`}></i>
                {sb.label}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-xl bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        {/* Subtext */}
        <div className="px-5 py-3 border-b border-background-200 bg-background-50">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[10px]">
            <div className="flex items-center gap-1.5 text-foreground-400">
              <i className="ri-calendar-line text-[9px]"></i>
              <span className="text-foreground-600 font-medium whitespace-nowrap">{message.sentAt.length > 16 ? message.sentAt.slice(0, 16) : message.sentAt}</span>
            </div>
            <div className="flex items-center gap-1.5 text-foreground-400">
              <i className="ri-user-line text-[9px]"></i>
              <span className="text-foreground-600 font-medium truncate">{message.targetLabel}</span>
            </div>
            {isSent && (
              <>
                <div className="flex items-center gap-1.5 text-foreground-400">
                  <i className="ri-check-double-line text-[9px]"></i>
                  <span className="text-foreground-600 font-medium">{message.deliveredCount} entregados</span>
                </div>
                <div className="flex items-center gap-1.5 text-foreground-400">
                  <i className="ri-mail-open-line text-[9px]"></i>
                  <span className="text-foreground-600 font-medium">{message.openedCount} abiertos</span>
                </div>
              </>
            )}
            {!isSent && (
              <>
                <div className="flex items-center gap-1.5 text-foreground-400">
                  <i className="ri-user-line text-[9px]"></i>
                  <span className="text-foreground-600 font-medium">Enviado por {message.sender}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="p-2 space-y-0.5">
          {/* Ver detalle */}
          <button
            onClick={() => { onView(message); onClose(); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-foreground-700 text-xs hover:bg-background-100 transition-colors cursor-pointer"
          >
            <div className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center">
              <i className="ri-eye-line text-foreground-500 text-sm"></i>
            </div>
            Ver detalle
          </button>

          {/* Ver estadísticas — solo enviados */}
          {isSent && (
            <button
              onClick={() => { onViewStats(message); onClose(); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-foreground-700 text-xs hover:bg-background-100 transition-colors cursor-pointer"
            >
              <div className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center">
                <i className="ri-bar-chart-line text-foreground-500 text-sm"></i>
              </div>
              Ver estadísticas
            </button>
          )}

          {/* Duplicar */}
          <button
            onClick={() => { onDuplicate(message); onClose(); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-foreground-700 text-xs hover:bg-background-100 transition-colors cursor-pointer"
          >
            <div className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center">
              <i className="ri-file-copy-line text-foreground-500 text-sm"></i>
            </div>
            Duplicar
          </button>

          {/* Editar — solo borradores */}
          {isDraft && (
            <button
              onClick={() => { onEdit(message); onClose(); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-foreground-700 text-xs hover:bg-background-100 transition-colors cursor-pointer"
            >
              <div className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center">
                <i className="ri-edit-line text-foreground-500 text-sm"></i>
              </div>
              Editar
            </button>
          )}

          {/* Programar — borradores */}
          {isDraft && (
            <button
              onClick={() => { onSchedule(message); onClose(); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sky-600 text-xs hover:bg-sky-50 transition-colors cursor-pointer"
            >
              <div className="w-7 h-7 rounded-lg bg-sky-50 flex items-center justify-center">
                <i className="ri-time-line text-sky-500 text-sm"></i>
              </div>
              Programar envío
            </button>
          )}

          {/* Reenviar — solo enviados */}
          {isSent && (
            <button
              onClick={() => { onResend(message); onClose(); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sky-600 text-xs hover:bg-sky-50 transition-colors cursor-pointer"
            >
              <div className="w-7 h-7 rounded-lg bg-sky-50 flex items-center justify-center">
                <i className="ri-send-plane-line text-sky-500 text-sm"></i>
              </div>
              Reenviar
            </button>
          )}

          {/* Separador — acciones críticas */}
          {(isDraft || isScheduled || isCancelled) && (
            <div className="my-1 border-t border-background-200 mx-3"></div>
          )}

          {/* Cancelar envío — solo programados */}
          {isScheduled && (
            <button
              onClick={() => { onCancelSend(message); onClose(); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-amber-600 text-xs hover:bg-amber-50 transition-colors cursor-pointer"
            >
              <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
                <i className="ri-close-circle-line text-amber-500 text-sm"></i>
              </div>
              Cancelar envío
            </button>
          )}

          {/* Eliminar — borradores, programados y cancelados */}
          {(isDraft || isScheduled || isCancelled) && (
            <button
              onClick={() => { onDelete(message); onClose(); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-500 text-xs hover:bg-red-50 transition-colors cursor-pointer"
            >
              <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center">
                <i className="ri-delete-bin-line text-red-500 text-sm"></i>
              </div>
              Eliminar
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-background-200 text-center">
          <button
            onClick={onClose}
            className="text-foreground-400 text-[10px] font-medium hover:text-foreground-600 transition-colors cursor-pointer"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}